import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useStore from "../../store/useStore";

const CategoryDropdown = ({ categories = [], onCategoryClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { selectedCategory, selectedCategoryData } = useStore();
  const [panelStyle, setPanelStyle] = useState({});
  const closeTimer = useRef(null);
  const wrapperRef = useRef(null);

  const searchParams = new URLSearchParams(location.search);
  const urlCategorySlug = searchParams.get("category");
  const activeSlug = urlCategorySlug || selectedCategory;

  // Derive the active category label based on current route and store
  const getCategoryLabel = () => {
    if (!location.pathname.startsWith("/products")) {
      return "Categories";
    }
    if (!activeSlug || activeSlug === "all") {
      return "Categories";
    }
    if (selectedCategoryData?.name && (!activeSlug || selectedCategoryData.slug === activeSlug)) {
      return selectedCategoryData.name;
    }
    const match = categories.find((c) => c.slug === activeSlug);
    return match ? match.name : "Categories";
  };

  const currentLabel = getCategoryLabel();

  const updatePanelPosition = () => {
    const anchor = wrapperRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    setPanelStyle({
      left: `${rect.left}px`,
      top: `${rect.bottom + 8}px`,
      width: `${Math.max(rect.width, 240)}px`,
    });
  };

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    updatePanelPosition();
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setIsOpen(false), 120);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);

    return () => {
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [isOpen]);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 rounded-xl bg-gradient-to-r from-primary-50 to-green-50 px-4 py-3 text-gray-800 transition-all hover:from-primary-100 hover:to-green-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 font-medium shadow-sm hover:shadow transition-shadow"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="flex items-center gap-2">
          <svg
            className="h-5 w-5 text-primary-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
          <span className="hidden sm:inline text-base font-bold">
            {currentLabel}
          </span>
        </div>
        <svg
          className={`h-4 w-4 text-gray-600 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="fixed z-50 max-h-[calc(100vh-140px)] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg"
          style={panelStyle}
        >
          <div className="py-2">
            <button
              onClick={() => {
                onCategoryClick("all");
                setIsOpen(false);
              }}
              className={`block w-full px-5 py-3 text-left font-medium transition-colors hover:bg-gradient-to-r hover:from-primary-50 hover:to-green-50 hover:text-primary-700 focus:bg-primary-50 focus:text-primary-700 focus:outline-none ${
                location.pathname.startsWith("/products") && (!activeSlug || activeSlug === "all")
                  ? "bg-primary-50 text-primary-700 font-bold"
                  : "text-gray-700"
              }`}
            >
              All Products
            </button>
            {categories.map((category) => {
              const isSelected =
                location.pathname.startsWith("/products") &&
                activeSlug === category.slug;

              return (
                <button
                  key={category.category_id}
                  onClick={() => {
                    onCategoryClick(category.slug);
                    setIsOpen(false);
                  }}
                  className={`block w-full border-b border-gray-50 px-5 py-3 text-left transition-colors last:border-b-0 hover:bg-gradient-to-r hover:from-primary-50 hover:to-green-50 hover:text-primary-700 focus:bg-primary-50 focus:text-primary-700 focus:outline-none ${
                    isSelected
                      ? "bg-primary-50 text-primary-700 font-bold"
                      : "text-gray-700"
                  }`}
                >
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Backdrop when dropdown is open on mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-30 sm:hidden"
          onClick={() => setIsOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default CategoryDropdown;
